import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../cn';

const alertStyles = cva('flex gap-3 rounded-lg border px-4 py-3 text-sm', {
  variants: {
    tone: {
      info: 'border-brand-200 bg-brand-50/60 text-brand-700',
      success: 'border-success-100 bg-success-50/60 text-success-600',
      warning: 'border-warning-100 bg-warning-50/60 text-warning-600',
      danger: 'border-danger-100 bg-danger-50/60 text-danger-600',
    },
  },
  defaultVariants: { tone: 'info' },
});

const icons = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
} as const;

export interface AlertProps extends VariantProps<typeof alertStyles> {
  title?: string;
  children?: ReactNode;
  className?: string;
}

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  const Icon = icons[tone ?? 'info'];
  return (
    <div role="alert" className={cn(alertStyles({ tone }), className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5')}>{children}</div>}
      </div>
    </div>
  );
}
